import { supabase } from '@/lib/supabase/client';
import type { DisplayConfigRemedyProduct, DisplayConfigRemedyProductInsert, DisplayConfigRemedyProductUpdate } from '@/types/display-config-remedy-products';

export const displayConfigRemedyProductsService = {
  async getByDisplayConfigId(displayConfigId: string): Promise<DisplayConfigRemedyProduct[]> {
    try {
      const { data, error } = await supabase
        .from('display_config_remedy_products')
        .select('*')
        .eq('display_config_id', displayConfigId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching display config Remedy products:', error);
        return [];
      }
      return (data as DisplayConfigRemedyProduct[]) || [];
    } catch (error) {
      console.error('Error fetching display config Remedy products:', error);
      return [];
    }
  },

  async create(data: DisplayConfigRemedyProductInsert): Promise<DisplayConfigRemedyProduct> {
    try {
      const { data: newProduct, error } = await supabase
        .from('display_config_remedy_products')
        .insert(data)
        .select()
        .single();

      if (error) {
        console.error('Error creating display config Remedy product:', error);
        throw new Error(`Failed to create display config Remedy product: ${error.message}`);
      }
      return newProduct as DisplayConfigRemedyProduct;
    } catch (error) {
      console.error('Error creating display config Remedy product:', error);
      throw error;
    }
  },

  async update(id: string, data: DisplayConfigRemedyProductUpdate): Promise<DisplayConfigRemedyProduct> {
    try {
      const { data: updatedProduct, error } = await supabase
        .from('display_config_remedy_products')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating display config Remedy product:', error);
        throw new Error(`Failed to update display config Remedy product: ${error.message}`);
      }
      if (!updatedProduct) {
        throw new Error('Display config Remedy product not found');
      }
      return updatedProduct as DisplayConfigRemedyProduct;
    } catch (error) {
      console.error('Error updating display config Remedy product:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('display_config_remedy_products')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting display config Remedy product:', error);
        throw new Error(`Failed to delete display config Remedy product: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting display config Remedy product:', error);
      throw error;
    }
  },

  async deleteByDisplayConfigId(displayConfigId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('display_config_remedy_products')
        .delete()
        .eq('display_config_id', displayConfigId);

      if (error) {
        console.error('Error deleting display config Remedy products by config ID:', error);
        throw new Error(`Failed to delete display config Remedy products: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting display config Remedy products by config ID:', error);
      throw error;
    }
  },

  async bulkCreate(displayConfigId: string, products: Array<{ component_id: string; quantity: number }>): Promise<DisplayConfigRemedyProduct[]> {
    try {
      const insertData = products.map(p => ({
        display_config_id: displayConfigId,
        component_id: p.component_id,
        quantity: p.quantity,
      }));

      if (insertData.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('display_config_remedy_products')
        .insert(insertData)
        .select();

      if (error) {
        console.error('Error bulk creating display config Remedy products:', error);
        throw new Error(`Failed to bulk create display config Remedy products: ${error.message}`);
      }
      return data as DisplayConfigRemedyProduct[];
    } catch (error) {
      console.error('Error bulk creating display config Remedy products:', error);
      throw error;
    }
  },
};

